'use client';

import { useEffect, useCallback, useState } from 'react';
import { absolutizeUrl } from '@containd/shared';
import { PinterestPinButton } from './SocialShareBar';
import { usePublicUi } from '@/lib/publicUi';
import styles from './PhotoLightbox.module.css';

export interface PhotoLightboxItem {
  fullUrl: string;
  label?: string;
}

export interface PhotoLightboxProps {
  photos: PhotoLightboxItem[];
  index: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
  pageUrl?: string;
}

/** Fullscreen swipeable / arrow-key photo viewer (Containd Photos tab lightbox twin). */
export function PhotoLightbox({
  photos,
  index,
  onClose,
  onIndexChange,
  pageUrl,
}: PhotoLightboxProps) {
  const { extra } = usePublicUi();
  const safeIndex = Math.max(0, Math.min(index, photos.length - 1));
  const current = photos[safeIndex];
  const [touchX, setTouchX] = useState<number | null>(null);

  const go = useCallback(
    (delta: number) => {
      if (photos.length === 0) return;
      onIndexChange((safeIndex + delta + photos.length) % photos.length);
    },
    [onIndexChange, photos.length, safeIndex],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  if (!current) return null;

  return (
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal
      aria-label={extra.sharePhotoViewer}
    >
      <button
        type="button"
        className={styles.close}
        onClick={onClose}
        aria-label={extra.close}
      >
        ×
      </button>
      {photos.length > 1 ? (
        <button
          type="button"
          className={styles.navLeft}
          aria-label={extra.sharePrevPhoto}
          onClick={(e) => {
            e.stopPropagation();
            go(-1);
          }}
        >
          ‹
        </button>
      ) : null}
      {photos.length > 1 ? (
        <button
          type="button"
          className={styles.navRight}
          aria-label={extra.shareNextPhoto}
          onClick={(e) => {
            e.stopPropagation();
            go(1);
          }}
        >
          ›
        </button>
      ) : null}
      <div
        className={styles.frame}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => setTouchX(e.changedTouches[0]?.clientX ?? null)}
        onTouchEnd={(e) => {
          const x = e.changedTouches[0]?.clientX;
          if (touchX == null || x == null) return;
          const dx = x - touchX;
          setTouchX(null);
          if (Math.abs(dx) < 40) return;
          go(dx < 0 ? 1 : -1);
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={current.fullUrl} alt={current.label ?? ''} />
        <div className={styles.meta}>
          <span>
            {safeIndex + 1}/{photos.length}
          </span>
          {current.label ? <span>{current.label}</span> : null}
          {pageUrl ? (
            <PinterestPinButton
              pageUrl={pageUrl}
              mediaUrl={absolutizeUrl(
                current.fullUrl,
                typeof window !== 'undefined' ? window.location.origin : '',
              )}
              description={current.label || ''}
              className={styles.pin}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
